import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Alert, Dialog, DialogContent, Grid, TextField, MenuItem,
  IconButton, Typography, Chip, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { DataTable } from '../common/DataTable';
import { SearchableEntitySelect, EntityOption } from '../common/SearchableEntitySelect';
import { SearchableMultiEntitySelect } from '../common/SearchableMultiEntitySelect';
import { PermissionMatrix, normalizePermissions, countViewModules } from './PermissionMatrix';
import { roleAPI, teamAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { catchyPrimaryButtonSx, catchyTextFieldSx } from '../../theme/formStyles';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants/tableDefaults';

type PermMap = Record<string, Record<string, boolean>>;

const emptyForm = () => ({
  name: '',
  description: '',
  roleId: '',
  status: 'active',
  members: [] as string[],
  leadId: '',
});

export const TeamsPanel: React.FC = () => {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [roles, setRoles] = useState<Array<{ _id: string; roleName: string; permissions?: PermMap }>>([]);
  const [memberOptions, setMemberOptions] = useState<EntityOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [permissions, setPermissions] = useState<PermMap>(normalizePermissions());

  const canCreate = hasPermission('teams', 'create');
  const canEdit = hasPermission('teams', 'edit');
  const canDelete = hasPermission('teams', 'delete');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [teamsRes, rolesRes, membersRes] = await Promise.all([
        teamAPI.list(),
        roleAPI.getAll(),
        teamAPI.members(),
      ]);
      setRows(teamsRes.data);
      setRoles(rolesRes.data || []);
      setMemberOptions((membersRes.data || []).map((u: Record<string, string>) => ({
        _id: String(u._id),
        label: u.name,
        sublabel: u.email,
      })));
    } catch {
      setError('Failed to load teams');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(emptyForm());
    setPermissions(normalizePermissions());
    setEditId(null);
    setOpen(true);
  };

  const openEdit = (row: Record<string, unknown>) => {
    setForm({
      name: String(row.name || ''),
      description: String(row.description || ''),
      roleId: String((row.roleId as { _id?: string })?._id || row.roleId || ''),
      status: String(row.status || 'active'),
      members: Array.isArray(row.members)
        ? row.members.map((m) => String((m as { _id?: string })._id || m))
        : [],
      leadId: String((row.leadId as { _id?: string })?._id || row.leadId || ''),
    });
    setPermissions(normalizePermissions(row.permissions as PermMap));
    setEditId(String(row._id));
    setOpen(true);
  };

  const applyRoleTemplate = (roleId: string) => {
    setForm((f) => ({ ...f, roleId }));
    const role = roles.find((r) => String(r._id) === roleId);
    if (role?.permissions) {
      setPermissions(normalizePermissions(role.permissions));
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Team name is required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        name: form.name.trim(),
        description: form.description,
        roleId: form.roleId || null,
        permissions,
        members: form.members,
        leadId: form.leadId || null,
        status: form.status,
      };
      if (editId) {
        await teamAPI.update(editId, payload);
        showToast('Team updated');
      } else {
        await teamAPI.create(payload);
        showToast('Team created');
      }
      setOpen(false);
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: Record<string, unknown>) => {
    if (!window.confirm(`Delete team "${row.name}"?`)) return;
    try {
      await teamAPI.remove(String(row._id));
      showToast('Team deleted');
      load();
    } catch {
      setError('Delete failed');
    }
  };

  return (
    <Box>
      {canCreate && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}
            sx={{ ...catchyPrimaryButtonSx, background: brandCss.gradientPrimary }}>
            Add Team
          </Button>
        </Box>
      )}

      {error && !open && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}

      <DataTable
        columns={[
          { field: 'name', header: 'Team' },
          {
            field: 'members',
            header: 'Members',
            render: (row) => {
              const count = Array.isArray(row.members) ? row.members.length : 0;
              return <Chip label={count} size="small" sx={{ height: 20, fontSize: 10.5 }} />;
            },
          },
          {
            field: 'roleId',
            header: 'Role Template',
            render: (row) => String((row.roleId as { roleName?: string })?.roleName || '—'),
          },
          {
            field: 'permissions',
            header: 'Access',
            render: (row) => `${countViewModules(normalizePermissions(row.permissions as PermMap))} modules`,
          },
          {
            field: 'leadId',
            header: 'Team Lead',
            render: (row) => String((row.leadId as { name?: string })?.name || '—'),
          },
          { field: 'status', header: 'Status' },
          {
            field: '_actions',
            header: '',
            render: (row) => (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {canEdit && (
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(row); }}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
                {canDelete && (
                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row); }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ),
          },
        ]}
        rows={rows}
        loading={loading}
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(0); }}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        recordLabel="teams"
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: `${RADIUS}px` } }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', bgcolor: brandCss.green50 }}>
          <Typography fontWeight={800} fontSize={16}>{editId ? 'Edit Team' : 'Create Team'}</Typography>
          <IconButton onClick={() => setOpen(false)}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ p: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField label="Team Name" required fullWidth size="small" sx={catchyTextFieldSx}
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Status" fullWidth size="small" sx={catchyTextFieldSx}
                value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Description" fullWidth size="small" sx={catchyTextFieldSx}
                value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label="Copy permissions from role (optional)" fullWidth size="small" sx={catchyTextFieldSx}
                value={form.roleId} onChange={(e) => applyRoleTemplate(e.target.value)}>
                <MenuItem value="">Custom permissions</MenuItem>
                {roles.map((r) => (
                  <MenuItem key={r._id} value={r._id}>{r.roleName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SearchableEntitySelect label="Team Lead" placeholder="Select lead..."
                value={form.leadId} options={memberOptions}
                onChange={(v) => setForm({ ...form, leadId: v })} />
            </Grid>
            <Grid item xs={12}>
              <SearchableMultiEntitySelect label="Team Members" placeholder="Add members..."
                value={form.members} options={memberOptions}
                onChange={(ids) => setForm({ ...form, members: ids })} />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography fontWeight={700} fontSize={12} sx={{ mb: 1 }}>Team Permissions</Typography>
              <PermissionMatrix permissions={permissions} onChange={setPermissions} />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving}
              sx={{ ...catchyPrimaryButtonSx, background: brandCss.gradientPrimary }}>
              {editId ? 'Update Team' : 'Create Team'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
